import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import autobind from 'autobind-decorator';
import Icon from './Icon';

export const Container = styled.div`
  flex-grow: 1;
`;

export const EditModeContainer = styled.div`
  flex-grow: 1;
  display: flex;
  position: relative;
  cursor: pointer;
`;

export const Input = styled.input`
  border: solid 1px ${({ theme }) => theme.colors.defaultActionBlue};
  transition: 100ms;
  height: ${({ inputPxHeight }) => inputPxHeight}px;
  font-size: ${({ inputPxHeight }) => inputPxHeight * 0.67}px;
  padding-left: 0px;
  padding-right: 45px;
  margin: 0 !important;
  border-radius: 3px !important;
  box-shadow: none !important;
  min-width: 0;
  -moz-appearance: textfield !important;
  flex-grow: 1;

  ${({ disguisedMode, theme }) =>
    disguisedMode
      ? css`
          background-color: ${theme.colors.white};
          border: solid 1px ${theme.colors.white};
        `
      : null}

  ${({ previewMode }) =>
    previewMode
      ? css`
          background-color: #f3f4f4;
          border: solid 1px #f3f4f4;
        `
      : null}
`;

export const EditButton = styled.div`
  background-color: #f3f4f4;
  height: 20px;
  width: 20px;
  border-radius: 2px;
  display: flex;
  opacity: ${({ hidden }) => (hidden ? '0' : '1')};
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  transition: 100ms;
  right: ${({ inputPxHeight }) => Math.max(inputPxHeight * 0.5 - 10, 0)}px;
  top: ${({ inputPxHeight }) => Math.max(inputPxHeight * 0.5 - 10, 0)}px;
`;

export const EditIcon = styled(Icon)`
  background-color: ${({ theme }) => theme.colors.mediumGrayText};
  color: #f3f4f4;
`;

export const UneditableField = styled.div`
  color: ${({ theme }) => theme.colors.black};
`;

export const ActionButtons = styled.div`
  display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
  width: 30px;
  justify-content: space-between;
  position: absolute;
  right: 4px;
  bottom: ${({ inputPxHeight }) => Math.max(inputPxHeight * 0.5 - 15, 3)}px;
`;

const ActionButton = styled.div`
  background-color: green;
  height: 14px;
  width: 14px;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

export const SubmitButton = styled(ActionButton)`
  background-color: ${({ theme }) => theme.colors.defaultActionBlue};
  transition: 100ms;

  &:hover {
    background-color: #1d76a4;
  }
`;

export const CancelButton = styled(ActionButton)`
  background-color: #f3f4f4;
  transition: 100ms;

  &:hover {
    background-color: #dcdee0;
  }
`;

export const SubmitIcon = styled(Icon)`
  color: #ffffff;
`;

export const CancelIcon = styled(Icon)`
  color: #3c424d;
`;

export default class EditableInput extends PureComponent {
  static propTypes = {
    value: PropTypes.string,
    editable: PropTypes.bool,
    onSubmitClick: PropTypes.func.isRequired,
    inputPxHeight: PropTypes.number,
  };

  static defaultProps = {
    value: '',
    editable: false,
    inputPxHeight: 40,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.value || '',
      editMode: false,
      hoveringSubmit: false,
      hoveringInput: false,
      lastSavedValue: props.value || '',
    };
  }

  @autobind
  handleEditButtonClick() {
    this.setState({
      editMode: true,
    });
    this.input.focus();
  }

  @autobind
  handleInputFocus() {
    this.setState({
      editMode: true,
    });
  }

  @autobind
  handleBlur() {
    const { hoveringSubmit, value, lastSavedValue } = this.state;

    if (hoveringSubmit) {
      this.props.onSubmitClick({
        previousValue: lastSavedValue,
        value,
      });
      this.setState({
        editMode: false,
        lastSavedValue: value,
      });
    } else {
      this.setState({
        editMode: false,
        hoveringInput: false,
        value: lastSavedValue,
      });
    }
  }

  @autobind
  handleCancelClick() {
    this.setState({
      value: this.state.lastSavedValue,
    });
  }

  @autobind
  handleInputMouseOver() {
    this.setState({ hoveringInput: true });
  }

  @autobind
  handleInputMouseLeave() {
    if (!this.state.editMode) {
      this.setState({ hoveringInput: false, editMode: false });
      this.input.blur();
    }
  }

  @autobind
  handleSubmitMouseOver() {
    this.setState({ hoveringSubmit: true });
  }

  @autobind
  handleSubmitMouseLeave() {
    this.setState({ hoveringSubmit: false });
  }

  @autobind
  handleInputUpdate(e) {
    this.setState({
      value: e.target.value,
    });
  }

  renderEditMode() {
    const { editMode, hoveringInput } = this.state;
    const { inputPxHeight } = this.props;

    return (
      <EditModeContainer
        onFocus={this.handleInputMouseOver}
        onMouseOver={this.handleInputMouseOver}
        onMouseLeave={this.handleInputMouseLeave}
      >
        <Input
          value={this.state.value}
          onFocus={this.handleInputFocus}
          onChange={this.handleInputUpdate}
          onBlur={this.handleBlur}
          previewMode={!editMode && hoveringInput}
          disguisedMode={!hoveringInput}
          innerRef={(c) => {
            this.input = c;
          }}
          inputPxHeight={inputPxHeight}
        />
        <EditButton
          onClick={this.handleEditButtonClick}
          inputPxHeight={inputPxHeight}
          hidden={editMode || !hoveringInput}
        >
          <EditIcon icon="edit-pencil" iconSize={12} />
        </EditButton>
        <ActionButtons hidden={!editMode} inputPxHeight={inputPxHeight}>
          <SubmitButton
            onFocus={this.handleSubmitMouseOver}
            onMouseOver={this.handleSubmitMouseOver}
            onMouseLeave={this.handleSubmitMouseLeave}
            data-selenium="submitEditButton"
          >
            <SubmitIcon icon="checkmark" iconSize={8} />
          </SubmitButton>
          <CancelButton onClick={this.handleCancelClick}>
            <CancelIcon icon="close-no-circle" iconSize={6} />
          </CancelButton>
        </ActionButtons>
      </EditModeContainer>
    );
  }

  render() {
    const { editable } = this.props;
    // TODO: @liuminosity build out non-editable mode

    return (
      <Container>
        {editable ? (
          this.renderEditMode()
        ) : (
          <UneditableField>{this.props.value}</UneditableField>
        )}
      </Container>
    );
  }
}
